import React, { useEffect, useRef, useState } from "react";
import { supabase } from "./auth.js";
import {
  F, BTN, LBL,
  loadPacks, deriveDisplayName,
  type Lang,
} from "./shared.js";

// ---------------------------------------------------------------------------
// EyeIcon
// ---------------------------------------------------------------------------

function EyeIcon({ show }: { show: boolean }) {
  return show ? (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// AccountSidebar
// ---------------------------------------------------------------------------

interface AccountSidebarProps {
  onClose: () => void;
  lang: Lang;
  onLangChange: (l: Lang) => void;
  email: string;
  userColor: string;
  avatarUrl: string;
  onAvatarChange: (url: string) => void;
  displayName: string;
  onDisplayNameChange: (name: string) => void;
  creditsUsed: number;
}

interface ProviderConnection {
  id: string;
  provider: string;
  displayName?: string;
  modelId?: string;
  maskedHint?: string;
  status: string;
}

export function AccountSidebar({ onClose, lang, onLangChange, email, userColor, avatarUrl, onAvatarChange, displayName, onDisplayNameChange, creditsUsed }: AccountSidebarProps) {
  const [connections,    setConnections]    = useState<ProviderConnection[]>([]);
  const [apiKey,         setApiKey]         = useState("");
  const [apiProvider,    setApiProvider]    = useState("claude");
  const [saveKeyStatus,  setSaveKeyStatus]  = useState<"idle"|"saving"|"error">("idle");
  const [company,      setCompany]      = useState(localStorage.getItem("studio_company_name")  ?? "");
  const [region,       setRegion]       = useState(localStorage.getItem("studio_region")        ?? "us");
  const [showSecurity, setShowSecurity] = useState(false);
  const [currentPwd,   setCurrentPwd]   = useState("");
  const [newPwd,       setNewPwd]       = useState("");
  const [confirmPwd,   setConfirmPwd]   = useState("");
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd,     setShowNewPwd]     = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const [editingNick, setEditingNick] = useState(false);
  const [nickDraft,   setNickDraft]   = useState("");
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [showSupport,    setShowSupport]    = useState(false);
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMsg,     setSupportMsg]     = useState("");
  const [supportStatus,  setSupportStatus]  = useState<"idle"|"sending"|"sent"|"error">("idle");

  const [referCopied,       setReferCopied]       = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const initials = displayName.slice(0, 2).toUpperCase();
  const packs    = loadPacks();

  const referLink = `https://studio.lanfinitas.ai/refer/${email.split("@")[0].slice(0, 8).toLowerCase().replace(/[^a-z0-9]/g, "")}`;

  function reloadConnections() {
    const token = localStorage.getItem("studio_token") ?? "";
    if (!token) return;
    fetch("/api/connections", { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then((data: { connections?: ProviderConnection[] }) => { if (data.connections) setConnections(data.connections); })
      .catch(() => {});
  }
  useEffect(reloadConnections, []);

  async function saveConnection() {
    const key = apiKey.trim();
    if (!key) return;
    setSaveKeyStatus("saving");
    const token = localStorage.getItem("studio_token") ?? "";
    try {
      const res = await fetch("/api/connections", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ provider: apiProvider, apiKey: key }),
      });
      if (!res.ok) { setSaveKeyStatus("error"); return; }
      setApiKey("");
      setSaveKeyStatus("idle");
      reloadConnections();
    } catch { setSaveKeyStatus("error"); }
  }

  async function deleteConnection(id: string) {
    const token = localStorage.getItem("studio_token") ?? "";
    try {
      await fetch(`/api/connections/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      setConnections(prev => prev.filter(c => c.id !== id));
    } catch { /* silent */ }
  }

  const AL = {
    en: {
      companyProfile: "COMPANY PROFILE",  companyName: "Company Name",
      preferences: "PREFERENCES",         language: "Language", region: "Region",
      trialCredits: "TRIAL CREDITS",      usageMonth: "USAGE THIS MONTH",
      planUsage: "PLAN & USAGE",          apiKey: "API KEY (BYOK)",
      provider: "Provider",              apiKeyLabel: "API Key",
      support: "SUPPORT",               referEarn: "REFER & EARN",
      legal: "LEGAL",                   dataPrivacy: "DATA & PRIVACY",
      saveKey: "SAVE KEY",              signOut: "SIGN OUT",
      noConnections: "No connections yet.", removeConnection: "Remove",
      storageNote: "Your key is encrypted and stored on our server, associated with your account — never sent to the browser again after saving.",
    },
    fr: {
      companyProfile: "PROFIL ENTREPRISE", companyName: "Nom de l'entreprise",
      preferences: "PRÉFÉRENCES",          language: "Langue",       region: "Région",
      trialCredits: "CRÉDITS D'ESSAI",     usageMonth: "UTILISATION CE MOIS",
      planUsage: "PLAN & USAGE",           apiKey: "CLÉ API (BYOK)",
      provider: "Fournisseur",            apiKeyLabel: "Clé API",
      support: "ASSISTANCE",            referEarn: "PARRAINAGE",
      legal: "MENTIONS LÉGALES",        dataPrivacy: "DONNÉES & VIE PRIVÉE",
      saveKey: "ENREGISTRER",           signOut: "DÉCONNEXION",
      noConnections: "Aucune connexion pour le moment.", removeConnection: "Supprimer",
      storageNote: "Votre clé est chiffrée et stockée sur notre serveur, associée à votre compte — plus jamais envoyée au navigateur après l'enregistrement.",
    },
    it: {
      companyProfile: "PROFILO AZIENDA",  companyName: "Nome azienda",
      preferences: "PREFERENZE",          language: "Lingua",        region: "Regione",
      trialCredits: "CREDITI DI PROVA",   usageMonth: "UTILIZZO DEL MESE",
      planUsage: "PIANO & UTILIZZO",      apiKey: "CHIAVE API (BYOK)",
      provider: "Provider",              apiKeyLabel: "Chiave API",
      support: "SUPPORTO",              referEarn: "REFERRAL",
      legal: "LEGALE",                  dataPrivacy: "DATI & PRIVACY",
      saveKey: "SALVA",                 signOut: "ESCI",
      noConnections: "Nessuna connessione ancora.", removeConnection: "Rimuovi",
      storageNote: "La tua chiave è crittografata e memorizzata sul nostro server, associata al tuo account — non più inviata al browser dopo il salvataggio.",
    },
    zh: {
      companyProfile: "公司信息",     companyName: "公司名称",
      preferences: "偏好设置",        language: "语言",          region: "地区",
      trialCredits: "试用额度",       usageMonth: "本月用量",
      planUsage: "套餐与用量",        apiKey: "自定义API密餉",
      provider: "服务商",            apiKeyLabel: "API密餉",
      support: "支持",              referEarn: "邀请返利",
      legal: "法律",                dataPrivacy: "数据与隐私",
      saveKey: "保存密餉",          signOut: "退出登录",
      noConnections: "暂无已保存的连接。", removeConnection: "删除",
      storageNote: "您的密钥已加密并存储在我们的服务器上，与您的账户关联 — 保存后不会再发送到浏览器。",
    },
  } as const;
  const L = AL[lang] ?? AL.en;

  const SEC: React.CSSProperties = { padding: "18px 20px", borderBottom: "1px solid #222" };
  const SEC_TITLE: React.CSSProperties = {
    fontFamily: "'Rama Gothic E', system-ui", fontSize: "0.6rem", letterSpacing: "0.15em",
    textTransform: "uppercase" as const, color: "var(--text-muted)", marginBottom: 12, display: "block",
  };
  const AINP: React.CSSProperties = {
    background: "#000", border: "1px solid #333", color: "#fff",
    fontFamily: "'Space Mono', monospace", fontSize: "0.6rem",
    padding: "7px 10px", width: "100%", outline: "none", boxSizing: "border-box" as const,
  };
  const ABTN: React.CSSProperties = {
    background: "transparent", border: "1px solid #333", color: "var(--text-muted)",
    fontFamily: "'Space Mono', monospace", fontSize: "0.55rem",
    letterSpacing: "0.1em", textTransform: "uppercase" as const, padding: "5px 10px", cursor: "pointer",
  };

  function patchProfile(updates: Record<string, unknown>) {
    const token = localStorage.getItem("studio_token") ?? "";
    if (!token) return;
    fetch("/api/profile", {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(updates),
    }).catch(() => {});
  }

  function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const url = ev.target?.result as string;
      localStorage.setItem("studio_avatar", url);
      onAvatarChange(url);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  function saveNickname() {
    const n = nickDraft.trim();
    localStorage.setItem("studio_nickname", n);
    const derived = deriveDisplayName(email, n);
    onDisplayNameChange(derived);
    patchProfile({ display_name: derived });
    setEditingNick(false);
  }

  async function sendSupport() {
    if (!supportMsg.trim()) return;
    setSupportStatus("sending");
    try {
      const res = await fetch("/api/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject: supportSubject, message: supportMsg, email }),
      });
      if (res.ok) {
        setSupportStatus("sent");
        setSupportSubject(""); setSupportMsg("");
        setTimeout(() => { setShowSupport(false); setSupportStatus("idle"); }, 3000);
      } else { setSupportStatus("error"); }
    } catch { setSupportStatus("error"); }
  }

  return (
    <>
      <div style={{ position:"fixed", inset:0, zIndex:40, background:"rgba(0,0,0,0.5)" }} />
      <div style={{ position:"fixed", top:0, right:0, bottom:0, width:380, zIndex:50, background:"var(--bg-surface)", borderLeft:"1px solid var(--border)", display:"flex", flexDirection:"column", overflow:"hidden" }}>

        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 20px", height:48, borderBottom:"1px solid var(--border)", flexShrink:0 }}>
          <span style={{ fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.6rem", fontWeight:"normal", letterSpacing:"0.2em", textTransform:"uppercase", color:"var(--text-muted)" }}>ACCOUNT</span>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:"var(--text-muted)", cursor:"pointer", fontSize:16, padding:0 }}>&#x2715;</button>
        </div>

        <div style={{ flex:1, overflowY:"auto" }}>

          {/* User header */}
          <div style={SEC}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <input ref={avatarInputRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handleAvatarUpload} />
              <button onClick={() => avatarInputRef.current?.click()} title="点击上传头像" style={{ width:48, height:48, borderRadius:"50%", background: avatarUrl ? "transparent" : userColor, color:"#0d0d0d", border:"none", fontSize:16, fontWeight:700, cursor:"pointer", flexShrink:0, overflow:"hidden", padding:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                {avatarUrl ? <img src={avatarUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} /> : initials}
              </button>
              <div style={{ minWidth:0, flex:1 }}>
                {editingNick ? (
                  <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:4 }}>
                    <input autoFocus type="text" value={nickDraft} onChange={e => setNickDraft(e.target.value)}
                      onKeyDown={e => { if (e.key==="Enter") saveNickname(); if (e.key==="Escape") setEditingNick(false); }}
                      placeholder={displayName} style={{ ...AINP, flex:1, width:"auto", fontSize:"0.65rem" }} />
                    <button onClick={saveNickname} style={{ background:"none", border:"none", color:"#aaa", cursor:"pointer", fontSize:14, padding:"0 2px" }}>✓</button>
                    <button onClick={() => setEditingNick(false)} style={{ background:"none", border:"none", color:"#777777", cursor:"pointer", fontSize:14, padding:"0 2px" }}>✕</button>
                  </div>
                ) : (
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                    <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.7rem", color:"var(--text)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{displayName}</div>
                    <button onClick={() => { setNickDraft(localStorage.getItem("studio_nickname") ?? ""); setEditingNick(true); }} title="编辑昵称" style={{ background:"none", border:"none", color:"#777777", cursor:"pointer", fontSize:11, padding:0, lineHeight:1, flexShrink:0 }}>✏️</button>
                  </div>
                )}
                <span style={{ fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.55rem", fontWeight:"normal", letterSpacing:"0.15em", border:"1px solid #ffffff", color:"#ffffff", padding:"2px 8px", textTransform:"uppercase" }}>TRIAL</span>
              </div>
            </div>
          </div>

          {/* Company Profile */}
          <div style={SEC}>
            <span style={SEC_TITLE}>{L.companyProfile}</span>
            <label style={LBL}>{L.companyName}</label>
            <input type="text" value={company} placeholder="Your company name"
              onChange={e => { setCompany(e.target.value); localStorage.setItem("studio_company_name", e.target.value); patchProfile({ company_name: e.target.value }); }} style={AINP} />
          </div>

          {/* Preferences */}
          <div style={SEC}>
            <span style={SEC_TITLE}>{L.preferences}</span>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <div>
                <label style={LBL}>{L.language}</label>
                <select value={lang} onChange={e => onLangChange(e.target.value as Lang)} style={{ ...AINP, appearance:"none" as React.CSSProperties["appearance"] }}>
                  <option value="en">EN — English</option>
                  <option value="fr">FR — Français</option>
                  <option value="it">IT — Italiano</option>
                  <option value="zh">中文</option>
                </select>
              </div>
              <div>
                <label style={LBL}>{L.region}</label>
                <select value={region} onChange={e => { setRegion(e.target.value); localStorage.setItem("studio_region", e.target.value); patchProfile({ region: e.target.value }); }} style={{ ...AINP, appearance:"none" as React.CSSProperties["appearance"] }}>
                  <option value="us">US</option>
                  <option value="eu">EU</option>
                  <option value="cn">CN</option>
                  <option value="ap">AP</option>
                </select>
              </div>
            </div>
          </div>

          {/* Trial Credits */}
          <div style={SEC}>
            <span style={SEC_TITLE}>{L.trialCredits}</span>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <span style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.6rem", color:"var(--text-muted)" }}>{creditsUsed} / 5 Studio Credits</span>
              <span style={{ fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.55rem", letterSpacing:"0.1em", color:"#888" }}>TRIAL</span>
            </div>
            <div style={{ height:3, background:"#2a2a2a", marginBottom:6 }}>
              <div style={{ width:`${Math.min(creditsUsed/5*100,100)}%`, height:"100%", background:"#ffffff" }} />
            </div>
            <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.55rem", color:"#666666" }}>
              {5 - creditsUsed} remaining — upgrade for more
            </div>
          </div>

          {/* Usage This Month */}
          <div style={SEC}>
            <span style={SEC_TITLE}>{L.usageMonth}</span>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <tbody>
                {[
                  { label:"AI Requests",   used:0,                    limit:50 },
                  { label:"Tech Packs",    used:packs.length,         limit:5  },
                  { label:"File Uploads",  used:0,                    limit:20 },
                ].map(row => (
                  <tr key={row.label} style={{ borderBottom:"1px solid #1a1a1a" }}>
                    <td style={{ padding:"6px 0", fontFamily:"'Space Mono', monospace", fontSize:"0.6rem", color:"var(--text-muted)" }}>{row.label}</td>
                    <td style={{ padding:"6px 0", fontFamily:"'Space Mono', monospace", fontSize:"0.6rem", color:"var(--text)", textAlign:"right" }}>{row.used} / {row.limit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Plan cards */}
          <div style={SEC}>
            <span style={SEC_TITLE}>{L.planUsage}</span>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:10 }}>
              {[
                { name:"BASIC",  price:"$19",  period:"/mo", credits:"3 packs"  },
                { name:"PRO",    price:"$69",  period:"/mo", credits:"12 packs" },
                { name:"STUDIO", price:"$199", period:"/mo", credits:"35 packs" },
              ].map(plan => (
                <div key={plan.name} style={{ border:"1px solid #ffffff", padding:"10px 8px", textAlign:"center", background:"#000" }}>
                  <div style={{ fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.5rem", letterSpacing:"0.12em", color:"#ffffff", marginBottom:4, textTransform:"uppercase" }}>{plan.name}</div>
                  <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.7rem", color:"#ffffff", marginBottom:2 }}>
                    {plan.price}<span style={{ fontSize:"0.5rem", color:"#aaaaaa" }}>{plan.period}</span>
                  </div>
                  <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.55rem", color:"#888" }}>{plan.credits}</div>
                </div>
              ))}
            </div>
            <button disabled style={{ background:"transparent", border:"none", fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.6rem", letterSpacing:"0.15em", textTransform:"uppercase" as const, color:"#666666", cursor:"not-allowed", padding:0 }}>VIEW PRICING →</button>
          </div>

          {/* API KEY (BYOK) — server-stored connections (Phase 3C item 2.3/2.4) */}
          <div style={SEC}>
            <span style={SEC_TITLE}>{L.apiKey}</span>
            {connections.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {connections.map(c => (
                  <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #1a1a1a" }}>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: "0.6rem", color: "#ccc" }}>
                      {c.provider} — {c.maskedHint ?? "••••"}
                    </span>
                    <button onClick={() => deleteConnection(c.id)} style={{ ...ABTN, padding: "3px 8px" }}>{L.removeConnection}</button>
                  </div>
                ))}
              </div>
            )}
            {connections.length === 0 && (
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: "0.55rem", color: "#666666", marginBottom: 10 }}>{L.noConnections}</div>
            )}
            <label style={LBL}>{L.provider}</label>
            <select value={apiProvider} onChange={e => setApiProvider(e.target.value)} style={{ ...AINP, marginBottom:8, appearance:"none" as React.CSSProperties["appearance"] }}>
              <option value="claude">Anthropic (Claude)</option>
              <option value="gemini">Google (Gemini)</option>
              <option value="mistral">Mistral AI</option>
              <option value="kimi">Moonshot (Kimi)</option>
              <option value="zhipu">Zhipu AI (GLM)</option>
            </select>
            <label style={LBL}>{L.apiKeyLabel}</label>
            <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="sk-…" style={{ ...AINP, marginBottom:8 }} />
            <button onClick={saveConnection} disabled={saveKeyStatus === "saving" || !apiKey.trim()} style={{ ...ABTN, width:"100%", textAlign:"center", marginBottom:6 }}>{L.saveKey}</button>
            {saveKeyStatus === "error" && <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.55rem", color:"#e05a5a", marginBottom:4 }}>Failed to save — check the key and try again.</div>}
            <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.55rem", color:"#666666" }}>{L.storageNote}</div>
          </div>

          {/* Security */}
          <div style={SEC}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: showSecurity ? 12 : 0 }}>
              <span style={SEC_TITLE} onClick={() => setShowSecurity(s => !s)}>SECURITY ▾</span>
            </div>
            {showSecurity && (
              <div>
                <label style={LBL}>CURRENT PASSWORD</label>
                <div style={{ position:"relative", marginBottom:8 }}>
                  <input type={showCurrentPwd ? "text" : "password"} value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} style={{ ...AINP, paddingRight:34 }} />
                  <button type="button" onClick={() => setShowCurrentPwd(v => !v)}
                    style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"#777777", cursor:"pointer", padding:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <EyeIcon show={showCurrentPwd} />
                  </button>
                </div>
                <label style={LBL}>NEW PASSWORD</label>
                <div style={{ position:"relative", marginBottom:8 }}>
                  <input type={showNewPwd ? "text" : "password"} value={newPwd} onChange={e => setNewPwd(e.target.value)} style={{ ...AINP, paddingRight:34 }} />
                  <button type="button" onClick={() => setShowNewPwd(v => !v)}
                    style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"#777777", cursor:"pointer", padding:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <EyeIcon show={showNewPwd} />
                  </button>
                </div>
                <label style={LBL}>CONFIRM NEW PASSWORD</label>
                <div style={{ position:"relative", marginBottom:8 }}>
                  <input type={showConfirmPwd ? "text" : "password"} value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} style={{ ...AINP, paddingRight:34 }} />
                  <button type="button" onClick={() => setShowConfirmPwd(v => !v)}
                    style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"#777777", cursor:"pointer", padding:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <EyeIcon show={showConfirmPwd} />
                  </button>
                </div>
                <button onClick={async () => {
                  if (!currentPwd || !newPwd || newPwd !== confirmPwd) return;
                  const { error } = await supabase.auth.updateUser({ password: newPwd });
                  if (!error) { setCurrentPwd(""); setNewPwd(""); setConfirmPwd(""); setShowSecurity(false); }
                }} style={{ ...ABTN, width:"100%", textAlign:"center" }}>UPDATE PASSWORD</button>
              </div>
            )}
          </div>

          {/* Support */}
          <div style={SEC}>
            <span style={SEC_TITLE}>{L.support}</span>
            {!showSupport ? (
              <button onClick={() => setShowSupport(true)} style={{ ...ABTN, width:"100%", textAlign:"center" }}>OPEN SUPPORT REQUEST</button>
            ) : (
              <div>
                <label style={LBL}>SUBJECT</label>
                <input type="text" value={supportSubject} onChange={e => setSupportSubject(e.target.value)}
                  placeholder="Brief description" style={{ ...AINP, marginBottom:8 }} />
                <label style={LBL}>MESSAGE</label>
                <textarea value={supportMsg} onChange={e => setSupportMsg(e.target.value)}
                  placeholder="Describe your issue…" rows={4}
                  style={{ ...AINP, resize:"vertical", marginBottom:8, fontFamily:"'Space Mono', monospace", lineHeight:1.6 }} />
                {supportStatus==="sent" ? (
                  <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.6rem", color:"#5ae09a" }}>✓ Support request sent</div>
                ) : (
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={sendSupport} disabled={supportStatus==="sending"} style={{ ...ABTN, flex:1, textAlign:"center" }}>
                      {supportStatus==="sending" ? "发送中…" : supportStatus==="error" ? "发送失败" : "SEND"}
                    </button>
                    <button onClick={() => { setShowSupport(false); setSupportStatus("idle"); }} style={{ ...ABTN, flex:1, textAlign:"center" }}>CANCEL</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Refer & Earn */}
          <div style={SEC}>
            <span style={SEC_TITLE}>{L.referEarn}</span>
            <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.6rem", color:"#888", lineHeight:1.8, marginBottom:10 }}>
              Invite a designer. They join, you get 2 free credits.<br />
              <span style={{ color:"#555", fontSize:"0.55rem" }}>Credits valid for 90 days. Non-transferable.</span>
            </div>
            <label style={LBL}>YOUR INVITE LINK</label>
            <div style={{ display:"flex", gap:6, marginBottom:8 }}>
              <input readOnly value={referLink}
                style={{ ...AINP, flex:1, width:"auto", color:"#555" }} />
              <button onClick={() => {
                void navigator.clipboard.writeText(referLink);
                setReferCopied(true);
                setTimeout(() => setReferCopied(false), 2000);
              }} style={{ ...ABTN, flexShrink:0 }}>
                {referCopied ? "COPIED ✓" : "COPY"}
              </button>
            </div>
            <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.55rem", color:"#555" }}>
              0 friends invited · 0 credits earned
            </div>
          </div>

          {/* Legal */}
          <div style={SEC}>
            <span style={SEC_TITLE}>{L.legal}</span>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {[
                { label:"Terms of Service (EU)",  href:"/legal/tos-eu"  },
                { label:"Terms of Service (US)",  href:"/legal/tos-us"  },
                { label:"Terms of Service (CN)",  href:"/legal/tos-cn"  },
                { label:"Beta NDA",               href:"/legal/beta"     },
                { label:"Refund & Credit Policy", href:"/legal/addenda"  },
                { label:"Reward Programme Terms", href:"/legal/beta"     },
              ].map(({ label, href }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.6rem", color:"#888", textDecoration:"none", display:"block" }}>
                  {label} →
                </a>
              ))}
            </div>
          </div>

          {/* Data & Privacy */}
          <div style={SEC}>
            <span style={SEC_TITLE}>{L.dataPrivacy}</span>
            <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.6rem", color:"#888", lineHeight:1.8 }}>
              During beta, anonymized data contribution is required.{" "}
              Covered under{" "}
              <a href="/legal/beta" target="_blank" rel="noopener noreferrer"
                style={{ color:"#888", textDecoration:"underline" }}>
                Beta NDA ↗
              </a>
            </div>
          </div>

          {/* Help / Docs */}
          <div style={SEC}>
            <span style={SEC_TITLE}>HELP & DOCS</span>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {[
                { label:"Documentation",    },
                { label:"Video Tutorials",  },
                { label:"Changelog",        },
                { label:"Status Page",      },
              ].map(({ label }) => (
                <div key={label} style={{ position:"relative", display:"inline-block" }}>
                  <span
                    style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.6rem", color:"#444", cursor:"not-allowed", display:"block" }}
                    title="Coming soon"
                  >
                    {label} →
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Sign Out */}
          <div style={{ padding:"18px 20px", borderBottom:"1px solid #222" }}>
            <button onClick={async () => { await supabase.auth.signOut(); window.location.href = "/"; }}
              style={{ ...ABTN, width:"100%", textAlign:"center", color:"#e05555", borderColor:"#e05555" }}>{L.signOut}</button>
          </div>

          {/* Danger Zone */}
          <div style={{ padding:"18px 20px" }}>
            <span style={{ ...SEC_TITLE, color:"#7a2020", marginBottom: showDeleteConfirm ? 10 : 0 }}>DANGER ZONE</span>
            {!showDeleteConfirm ? (
              <button onClick={() => setShowDeleteConfirm(true)}
                style={{ ...ABTN, width:"100%", textAlign:"center", color:"#e05555", borderColor:"#2a0000" }}>
                DELETE ACCOUNT
              </button>
            ) : (
              <div>
                <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.6rem", color:"#e05555", lineHeight:1.8, marginBottom:10 }}>
                  This will permanently delete your account and all data. This cannot be undone.
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  <button disabled
                    style={{ ...ABTN, flex:1, textAlign:"center", color:"#e05555", borderColor:"#e05555", cursor:"not-allowed" }}>
                    CONFIRM DELETE
                  </button>
                  <button onClick={() => setShowDeleteConfirm(false)}
                    style={{ ...ABTN, flex:1, textAlign:"center" }}>
                    CANCEL
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}
